import React, { useState } from "react";
import { X, MapPin, ShieldCheck, GraduationCap, Ruler, Heart, ChevronLeft, ChevronRight, MessageCircle, Images } from "lucide-react";
import { resolveImg } from "../lib/api";
import { personalLabel } from "../lib/profileOptions";

const Tag = ({ children, tone = "terracotta" }) => (
  <span className={`px-3 py-1 rounded-full text-xs font-medium ${tone === "sage" ? "bg-kora-sage/10 text-kora-sage" : "bg-kora-terracotta/10 text-kora-terracotta"}`}>{children}</span>
);

const ProfileDetailModal = ({ profile, onClose, onAction, onMessage }) => {
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(false);
  if (!profile) return null;

  const photos = (profile.photos && profile.photos.length ? profile.photos : [profile.photo_url]).filter(Boolean);
  const total = photos.length;
  const go = (dir) => setIdx((i) => (i + dir + total) % total);

  const personalItems = [
    { key: "marital_status", label: "Situation", value: profile.marital_status },
    { key: "has_children", label: "Enfants", value: profile.has_children },
    { key: "languages", label: "Langue", value: profile.languages },
    { key: "smoking", label: "Tabac", value: profile.smoking },
    { key: "alcohol", label: "Alcool", value: profile.alcohol },
  ].map((it) => ({ ...it, display: personalLabel(it.key, it.value) })).filter((it) => it.display);

  return (
    <div className="fixed inset-0 z-[70] bg-kora-ink/80 backdrop-blur-sm flex items-end md:items-center justify-center" data-testid="profile-detail-modal" onClick={onClose}>
      <div className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl max-h-[90vh] overflow-y-auto kora-scroll animate-float-up" onClick={(e) => e.stopPropagation()}>
        <div className="relative">
          <img src={resolveImg(photos[idx])} alt={profile.name} onClick={() => setZoom(true)} className="w-full h-72 object-cover cursor-zoom-in" data-testid="detail-photo" />
          <div className="absolute inset-0 bg-gradient-to-t from-kora-ink/70 to-transparent pointer-events-none" />
          <button data-testid="detail-close" onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center"><X size={18} /></button>

          {total > 1 && (
            <>
              <button data-testid="detail-photo-prev" onClick={() => go(-1)} className="absolute top-1/2 left-2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center"><ChevronLeft size={20} /></button>
              <button data-testid="detail-photo-next" onClick={() => go(1)} className="absolute top-1/2 right-2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center"><ChevronRight size={20} /></button>
              <div className="absolute top-3 left-0 w-full flex justify-center gap-1.5 px-12">
                {photos.map((_, i) => (
                  <span key={i} className={`h-1 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-3 bg-white/50"}`} />
                ))}
              </div>
            </>
          )}

          <div className="absolute bottom-4 left-5 right-5 text-white">
            <div className="flex items-center gap-2">
              <h2 className="font-display font-black text-2xl">{profile.name}{profile.age ? `, ${profile.age}` : ""}</h2>
              {profile.verified && <ShieldCheck size={18} className="text-white bg-kora-sage rounded-full p-0.5" />}
            </div>
            <div className="flex items-center gap-3 text-white/85 text-sm mt-1">
              {profile.city && <span className="flex items-center gap-1"><MapPin size={13} /> {profile.city}{profile.residence_country ? ` → ${profile.residence_country}` : ""}</span>}
              {profile.body_type && <span>• {profile.body_type}</span>}
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex flex-wrap gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${profile.goal === "mariage" ? "bg-kora-sage/15 text-kora-sage" : "bg-kora-ochre/15 text-[#C77826]"}`}>
              {profile.goal === "mariage" ? "Mariage" : "Amicale"}
            </span>
            {profile.height && <span className="flex items-center gap-1 text-xs font-medium text-kora-ink/70"><Ruler size={13} /> {profile.height} cm</span>}
            {profile.education && <span className="flex items-center gap-1 text-xs font-medium text-kora-ink/70"><GraduationCap size={13} /> {profile.education}</span>}
          </div>

          <button
            data-testid="detail-photos-btn"
            onClick={() => { setIdx(0); setZoom(true); }}
            className="w-full flex items-center justify-center gap-2 bg-kora-ink text-white rounded-full py-3 font-bold text-sm transition-transform hover:scale-[1.02]"
          >
            <Images size={17} /> Mes photos ({total})
          </button>

          {profile.bio && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-kora-muted mb-1">À propos</p>
              <p className="text-sm text-kora-ink/80">{profile.bio}</p>
            </div>
          )}

          {profile.interests?.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-kora-muted mb-2">Centres d'intérêt</p>
              <div className="flex flex-wrap gap-2">{profile.interests.map((i) => <Tag key={i}>{i}</Tag>)}</div>
            </div>
          )}

          {profile.partner_values?.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-kora-muted mb-2">Recherché chez un partenaire</p>
              <div className="flex flex-wrap gap-2">{profile.partner_values.map((v) => <Tag key={v} tone="sage">{v}</Tag>)}</div>
            </div>
          )}

          {personalItems.length > 0 && (
            <div data-testid="detail-personal">
              <p className="text-xs font-bold uppercase tracking-wider text-kora-muted mb-2">Informations personnelles</p>
              <div className="grid grid-cols-2 gap-2">
                {personalItems.map((it) => (
                  <div key={it.key} className="bg-kora-sand rounded-xl px-3 py-2" data-testid={`detail-personal-${it.key}`}>
                    <p className="text-[10px] uppercase tracking-wider text-kora-muted">{it.label}</p>
                    <p className="text-sm font-semibold text-kora-ink">{it.display}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(onAction || onMessage) && (
            <div className="pt-2 space-y-3 sticky bottom-0 bg-white">
              {onMessage && (
                <button
                  data-testid="detail-message-btn"
                  onClick={() => onMessage(profile)}
                  className="w-full flex items-center justify-center gap-2 bg-kora-sage text-white rounded-full py-3 font-bold transition-transform hover:scale-[1.02]"
                >
                  <MessageCircle size={18} /> Envoyer un message
                </button>
              )}
              {onAction && (
                <div className="flex gap-3">
                  <button
                    data-testid="detail-pass-btn"
                    onClick={() => onAction("pass")}
                    className="flex-1 flex items-center justify-center gap-2 border-2 border-kora-line text-kora-ink rounded-full py-3 font-bold transition-colors hover:bg-kora-sand"
                  >
                    <X size={18} /> Passer
                  </button>
                  <button
                    data-testid="detail-like-btn"
                    onClick={() => onAction("like")}
                    className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-kora-terracotta to-kora-ochre text-white rounded-full py-3 font-bold transition-transform hover:scale-[1.02]"
                  >
                    <Heart size={18} fill="currentColor" /> J'aime
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {zoom && (
        <div
          className="fixed inset-0 z-[85] bg-black/95 flex items-center justify-center"
          data-testid="photo-lightbox"
          onClick={(e) => { e.stopPropagation(); setZoom(false); }}
        >
          <button data-testid="lightbox-close" onClick={(e) => { e.stopPropagation(); setZoom(false); }} className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/15 text-white flex items-center justify-center"><X size={22} /></button>
          <img src={resolveImg(photos[idx])} alt={profile.name} className="max-w-full max-h-full object-contain" data-testid="lightbox-image" onClick={(e) => e.stopPropagation()} />
          {total > 1 && (
            <>
              <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute top-1/2 left-3 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 text-white flex items-center justify-center"><ChevronLeft size={24} /></button>
              <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute top-1/2 right-3 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 text-white flex items-center justify-center"><ChevronRight size={24} /></button>
              <div className="absolute bottom-6 left-0 w-full flex justify-center gap-1.5">
                {photos.map((_, i) => (
                  <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-3 bg-white/40"}`} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileDetailModal;
